import type { QueryKey } from "@tanstack/react-query";
import { createContext } from "react";

/**
 * **SEC-2 — client state is scoped to the account that read it** (A146
 * finding 4+5, T-910).
 *
 * The app has one `QueryClient` for the life of the tab (`App.tsx`), and the
 * providers above `<Routes>` never remount. So without a key that names the
 * account, a second account signing in to the same tab is served the first
 * one's cached rows (profile, daily reward, inventory, the lobby's viewer
 * block) until its own reads land — and a read the first account started can
 * land after the switch, into the entry the second one reads.
 *
 * ── The generation is the player id ──────────────────────────────────────────
 *
 * Chosen over a counter because the account IS the thing the cache must
 * separate, and a player id separates it **by construction**: two accounts can
 * never share a key, whatever order their reads resolve in, and nothing has to
 * remember to bump anything. A counter would separate a logout from the next
 * sign-in only if every identity change remembered to bump it.
 *
 * The one case a raw `profile?.id` gets wrong is the **guest conversion**: the
 * handoff signs the guest out (the server revokes the session) and the player
 * signs back in to the SAME player row. Keyed on the bare id, the cache would
 * flip to "nobody" and back and the provider state would be thrown away for an
 * account that never changed. So `SessionProvider` holds the id **stickily**:
 * a signed-out read keeps the last player's generation, and only two things
 * move it — a different player id arriving, or a player-chosen log out
 * (`useLogout` → `forgetIdentity()`), which also removes every private query.
 *
 * ── Private vs public ────────────────────────────────────────────────────────
 *
 * A key is **private** when the response depends on the session cookie, and
 * every private key is built by {@link privateKey}: `[generation, ...key]`,
 * with the generation first so that {@link isPrivateQueryKey} can find every
 * one of them without a list to keep in sync. Public keys (the shop catalog)
 * are unchanged. The classification, route by route, is T-910 arm 2's
 * evidence (`qa-evidence/T-910/t910-arm2-keys.md`).
 */

export const IDENTITY_PREFIX = "identity:";

/** The generation a private query key carries as its first element. */
export type IdentityGeneration = `identity:${string}`;

/** Nobody has signed in in this tab since the last log out. */
export const ANONYMOUS_IDENTITY: IdentityGeneration = `${IDENTITY_PREFIX}anonymous`;

/**
 * The generation, published by `SessionProvider` (the one writer) and read by
 * `useIdentity()` (`hooks/usePrivateKey.ts`). The default is the anonymous
 * generation: outside a `SessionProvider` there is no account, so a private
 * read there is nobody's. The app mounts every screen inside one (`App.tsx`);
 * the default is what a component test that renders a hook bare, or mocks
 * `useSession`, gets — the same key on every render, as a real account's is.
 */
export const IdentityContext = createContext<IdentityGeneration>(ANONYMOUS_IDENTITY);

export function identityOf(playerId: string | null): IdentityGeneration {
  return playerId === null ? ANONYMOUS_IDENTITY : `${IDENTITY_PREFIX}${playerId}`;
}

/** `[generation, ...key]` — the only way a private query key is spelled. */
export function privateKey<const K extends readonly unknown[]>(
  generation: IdentityGeneration,
  key: K,
): readonly [IdentityGeneration, ...K] {
  return [generation, ...key] as const;
}

/** True for every key {@link privateKey} built, whatever account it names. */
export function isPrivateQueryKey(key: QueryKey): boolean {
  const head = key[0];
  return typeof head === "string" && head.startsWith(IDENTITY_PREFIX);
}
